import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useSelector } from "react-redux";
import { useFormikContext } from "formik";
import { Button, Row } from "@masterteam/mt-design-react";

import Table from "../../NewTable";
import RiskForm from "./RiskForm";
import { formatDate, getLookupName } from "../../../helpers";

import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete.svg";

import "./style.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

const Risks = ({ lists, isReadOnly }) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";
  const { values, setFieldValue } = useFormikContext();
  const [formModal, setFormModal] = useState(initialModalState);
  const [risks, setRisks] = useState(values.Risks);

  const [RiskLikelihood, RiskImpact, RiskCategory, RiskStatus] = useSelector(
    ({ theme }) => [
      theme.RiskLikelihood.item.Items,
      theme.RiskImpact.item.Items,
      theme.RiskCategory.item.Items,
      theme.RiskStatus.item.Items,
    ]
  );

  const columns = [
    {
      key: "ID",
      cell: (item) => risks?.findIndex((x) => x.ID === item?.ID) + 1,
      header: intl.formatMessage({ id: "risk.code" }),
      width: "5%",
    },
    {
      key: "RiskName",
      cell: (item) => (isAr ? item?.ArabicName : item?.EnglishName),
      header: intl.formatMessage({ id: "common.risk-name" }),
      width: "20%",
    },
    {
      key: "Probability",
      cell: (item) =>
        getLookupName(RiskLikelihood, `${item?.Likelihood}`, isAr),
      header: intl.formatMessage({ id: "risk.probability" }),
      width: "12.5%",
    },
    {
      key: "Impact",
      cell: (item) => getLookupName(RiskImpact, `${item?.Impact}`, isAr),
      header: intl.formatMessage({ id: "risk.impact" }),
      width: "12.5%",
    },
    {
      key: "Classification",
      cell: (item) => getLookupName(RiskCategory, `${item?.Category}`, isAr),
      header: intl.formatMessage({ id: "risk.category" }),
      width: "12.5%",
    },
    {
      key: "DueDate",
      cell: (item) => formatDate(item?.DueDate),
      header: intl.formatMessage({ id: "common.end-date" }),
      width: "12.5%",
    },
    {
      key: "MitigationPlan",
      cell: (item) => item.MitigationPlan,
      header: intl.formatMessage({ id: "risk.mitigation-plan" }),
      width: "20%",
    },
    {
      key: "Status",
      cell: (item) => getLookupName(RiskStatus, `${item?.Status}`, isAr),
      header: intl.formatMessage({ id: "common.status" }),
      width: "5%",
    },
  ];

  const actions = [
    {
      key: "edit",
      variant: "primary",
      Icon: EditIcon,
      "tooltip-position": "buttom",
      tooltip: "Update",
      onClick: (item) => {
        setFormModal({ isOpen: true, data: item });
      },
    },
    {
      key: "delete",
      variant: "error",
      Icon: DeleteIcon,
      "tooltip-position": "buttom",
      tooltip: "Delete",
      onClick: (item) => {
        setRisks(risks.filter((e) => e.ID !== item.ID));
      },
    },
  ];

  useEffect(() => {
    setFieldValue("Risks", risks);
  }, [risks]);

  return (
    <div className="risks">
      <Row align="center" justify="between">
        <div className="title-wrapper">
          {intl.formatMessage({ id: "common.risks" })}
        </div>

        {!isReadOnly && (
          <Button
            shape="round"
            onClick={() => setFormModal({ isOpen: true, data: null })}
          >
            {intl.formatMessage({ id: "common.add" })}
          </Button>
        )}
      </Row>
      <Table
        data={risks}
        columns={columns}
        actions={isReadOnly ? [] : actions}
        headerHasBackgroundColor
      />

      {formModal.isOpen && (
        <RiskForm
          data={formModal.data}
          lists={lists}
          risks={risks}
          setRisks={setRisks}
          closeModal={() => setFormModal(initialModalState)}
        />
      )}
    </div>
  );
};

export default Risks;
